import { useCallback, useEffect, useState } from 'react';
import { Box, CircularProgress, Alert, Button } from '@mui/material';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { observer } from 'mobx-react';
import {
  IconLibrary,
  Layout,
  StackablePageButtons,
  PageSection,
  getDefaultValuesForForm,
} from '@product-framework/index';
import {
  Breadcrumb,
  FORM_ACTION,
  useDataProvider,
  useLocalization,
  useSnackbar,
  useStore,
} from '@ui-framework/index';
import { useNavigate, useParams } from 'react-router-dom';
import DefinitionFlow from './DefinitionFlow';
import { createSchema, editSchema } from '../../schemas';
import INSIGHTS_PATH from '../../apiPaths';
import {
  getFlowDefinitionsDataById,
  preProcessRequestData,
  preProcessFetchData,
} from './lib';

const FLOW_DEFINITION_FORM_STORE_NAME = 'flowDefinitionFormStore';

const DEFAULT_DEFINITION_JSON = { nodes: [], start: 'trigger' };

function FlowDefinitionMaintenance({ action = FORM_ACTION.CREATE }) {
  const [formValues, setFormValues] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const { formatMessage } = useLocalization();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { post, put, get, getApiUrl } = useDataProvider(provider);
  const store = useStore();
  const { id: pageId = null } = useParams();

  const writeApiUrl = `${getApiUrl()}${INSIGHTS_PATH}`;
  const updateApiUrl = pageId ? `${writeApiUrl}/${pageId}` : null;

  const isEditForm = action === FORM_ACTION.EDIT;
  const schema = isEditForm ? editSchema : createSchema;

  const params = {
    post,
    get,
    getApiUrl,
    formatMessage,
  };

  const fetchDefaultValues = useCallback(async () => {
    try {
      let result;
      if (isEditForm) {
        result = await getFlowDefinitionsDataById(params, pageId);
      } else {
        result = {
          data: {
            ...getDefaultValuesForForm({
              schema,
            }),
          },
          errors: null,
        };
      }
      // Set form values on load so React Flow shows immediately; ensure definitionJson has a default so the canvas always renders
      if (result?.data) {
        setFormValues({
          ...result.data,
          definitionJson: result.data.definitionJson ?? DEFAULT_DEFINITION_JSON,
        });
      }
      return result;
    } catch (err) {
      return {
        data: null,
        errors: [{ message: err.message }],
      };
    }
  }, [isEditForm, pageId, schema, get, getApiUrl, formatMessage]);

  const loadFlowDefinition = useCallback(
    (getIsCancelled = () => false) => {
      const loadErrorMsg = formatMessage({
        id: 'FLOWDEFINITION_LOAD_ERROR',
        defaultMessage: 'Failed to load flow definition',
      });
      setFetchError(null);
      setLoading(true);
      fetchDefaultValues()
        .then(result => {
          if (getIsCancelled()) return;
          setLoading(false);
          if (result?.errors?.length) {
            setFetchError(result.errors[0]?.message || loadErrorMsg);
          }
        })
        .catch(err => {
          if (getIsCancelled()) return;
          setLoading(false);
          setFetchError(err?.message || loadErrorMsg);
        });
    },
    [fetchDefaultValues, formatMessage],
  );

  useEffect(() => {
    let cancelled = false;
    loadFlowDefinition(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [pageId, isEditForm]);

  const onSubmitCreateForm = async data => {
    const requestData = preProcessRequestData(data);
    const response = await post(writeApiUrl, { ...requestData });

    if (response?.ok) {
      snackbar.showMessage(
        formatMessage({
          id: 'FLOWDEFINITION_CREATE_SUCCESS',
          defaultMessage: 'Flow Definition was created successfully',
        }),
      );
      return {
        nextValues: response?.data,
        errors: null,
        callback: () =>
          navigate(`/flow-definitions/edit/${response?.data?.id}`),
      };
    }
    return {
      nextValues: data,
      errors: response?.errors,
    };
  };

  const onSubmitEditForm = async data => {
    const requestData = preProcessRequestData(data);
    const response = await put(updateApiUrl, { ...requestData });
    if (response?.ok) {
      snackbar.showMessage(
        formatMessage({
          id: 'FLOWDEFINITION_UPDATE_SUCCESS',
          defaultMessage: 'Flow Definition was saved successfully',
        }),
      );
      return {
        nextValues: preProcessFetchData(response?.data),
        errors: null,
      };
    }
    return {
      nextValues: data,
      errors: response?.errors,
    };
  };

  const onSubmit = async data => {
    setFormValues(data);
    try {
      if (isEditForm) {
        return onSubmitEditForm(data);
      }
      return onSubmitCreateForm(data);
    } catch (err) {
      return {
        nextValues: data,
        errors: [{ message: err.message }],
      };
    }
  };

  const breadCrumb = [
    {
      name: formatMessage({
        id: 'DASHBOARD_BREADCRUMB',
        defaultMessage: 'Dashboard',
      }),
      icon: IconLibrary.Dashboard,
      onClick: () => {
        store.uiState[FLOW_DEFINITION_FORM_STORE_NAME]?.set({});
        navigate('/dashboard');
      },
    },
    {
      name: formatMessage({
        id: 'FLOWDEFINITIONS_BREADCRUMB',
        defaultMessage: 'Flow Definitions',
      }),
      icon: IconLibrary.Flow,
      onClick: () => {
        store.uiState[FLOW_DEFINITION_FORM_STORE_NAME]?.set({});
        navigate('/flow-definitions');
      },
    },
    isEditForm
      ? {
          name: formValues?.name
            ? `${formatMessage({
                id: 'EDIT_FLOWDEFINITION_EDIT_BREADCRUMB',
                defaultMessage: 'Edit Flow Definition',
              })}: ${formValues.name}`
            : formatMessage({
                id: 'EDIT_FLOWDEFINITION_EDIT_BREADCRUMB',
                defaultMessage: 'Edit Flow Definition',
              }),
          icon: IconLibrary.Flow,
          onClick: undefined,
        }
      : {
          name: formatMessage({
            id: 'CREATE_FLOWDEFINITION_BREADCRUMB',
            defaultMessage: 'Create Flow Definition',
          }),
          icon: IconLibrary.Flow,
          onClick: undefined,
        },
  ];

  const stackablePageButtons = [
    {
      label: formatMessage({
        id: 'EDIT_FLOWDEFINITIONS_STACKABLE_BUTTON',
        defaultMessage: 'List Flow Definitions',
      }),
      Icon: IconLibrary.Flow,
      variant: 'outlined',
      onClick: () => {
        store.uiState[FLOW_DEFINITION_FORM_STORE_NAME]?.set({});
        navigate('/flow-definitions');
      },
    },
  ];

  const editHeading = formatMessage({
    id: 'FLOWDEFINITION_EDIT_HEADING',
    defaultMessage: 'Edit Flow Definition',
  });
  const createHeading = formatMessage({
    id: 'FLOWDEFINITION_CREATE_HEADING',
    defaultMessage: 'Create Flow Definition',
  });
  const editLabelWithName = formValues?.name
    ? `${editHeading}: ${formValues.name}`
    : editHeading;
  const pageSectionLabel = isEditForm ? editLabelWithName : createHeading;

  return (
    <Layout>
      <Grid2 xs={12} container>
        <Grid2 xs={12} md={6}>
          <Breadcrumb items={breadCrumb} />
        </Grid2>
        <Grid2 xs={12} md={6}>
          <StackablePageButtons buttons={stackablePageButtons} />
        </Grid2>
      </Grid2>
      <PageSection label={pageSectionLabel}>
        <Box>
          {loading && (
            <Box
              mt={4}
              sx={{
                minHeight: '80vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'grey.50',
                borderRadius: 2,
              }}>
              <CircularProgress />
              <Box component="span" sx={{ ml: 2 }}>
                {formatMessage({
                  id: 'FLOWDEFINITION_LOADING',
                  defaultMessage: 'Loading flow definition…',
                })}
              </Box>
            </Box>
          )}
          {!loading && fetchError && (
            <Box mt={4}>
              <Alert
                severity="error"
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => loadFlowDefinition()}>
                    {formatMessage({
                      id: 'FLOWDEFINITION_RETRY',
                      defaultMessage: 'Retry',
                    })}
                  </Button>
                }>
                {fetchError}
              </Alert>
            </Box>
          )}
          {!loading && !fetchError && formValues?.definitionJson && (
            <Box mt={4}>
              <DefinitionFlow data={formValues} onSubmit={onSubmit} />
            </Box>
          )}
        </Box>
      </PageSection>
    </Layout>
  );
}

export default observer(FlowDefinitionMaintenance);
